import React, { useState, useEffect } from 'react'
import { Zap, Play, Clock, CheckCircle, AlertCircle } from 'lucide-react'
import { apiFetch } from '../config'

const Workflows = () => {
  const [workflows, setWorkflows] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newWorkflow, setNewWorkflow] = useState({ name: '', description: '' })

  useEffect(() => {
    fetchWorkflows()
  }, [])

  const fetchWorkflows = async () => {
    try {
      const response = await apiFetch("/workflows")
      const data = await response.json()
      setWorkflows(data)
      setLoading(false)
    } catch (error) {
      console.error('Failed to fetch workflows:', error)
      setLoading(false)
    }
  }

  const handleCreateWorkflow = async () => {
    if (!newWorkflow.name.trim()) return
    
    try {
      const response = await apiFetch("/workflows", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newWorkflow)
      })
      
      if (response.ok) {
        setShowCreateModal(false)
        setNewWorkflow({ name: '', description: '' })
        fetchWorkflows()
      } else {
        alert('Failed to create workflow')
      }
    } catch (error) {
      console.error('Failed to create workflow:', error)
      alert('Error creating workflow')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Antfarm Workflows</h1>
          <p className="text-text-secondary mt-1">
            Orchestrate multi-agent workflows and automation
          </p>
        </div>
        
        <button 
          className="btn-primary flex items-center gap-2"
          onClick={() => setShowCreateModal(true)}
        >
          <Zap className="w-4 h-4" />
          Create Workflow
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        </div>
      ) : workflows.length === 0 ? (
        <div className="card p-12 text-center">
          <Zap className="w-16 h-16 mx-auto text-text-secondary mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Workflows Found</h2>
          <p className="text-text-secondary mb-6">
            Create your first Antfarm workflow to orchestrate agents
          </p>
          <button className="btn-primary">
            Create First Workflow
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {workflows.map((workflow) => (
            <div key={workflow.id} className="card">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Zap className="w-6 h-6 text-accent" />
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-lg">{workflow.name}</h3>
                    <p className="text-text-secondary">{workflow.description}</p>
                    
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      <span className="flex items-center gap-1 text-text-secondary">
                        <Clock className="w-4 h-4" />
                        Last run: Never
                      </span>
                      
                      <span className="flex items-center gap-1 text-text-secondary">
                        <CheckCircle className="w-4 h-4" />
                        Success rate: N/A
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button className="btn-secondary flex items-center gap-2">
                    <Play className="w-4 h-4" />
                    Run
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card mt-8">
        <h2 className="text-lg font-semibold mb-4">Active Workflows</h2>
        
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 mx-auto text-text-secondary mb-3" />
          <p className="text-text-secondary">No workflows currently running</p>
        </div>
      </div>

      {/* Create Workflow Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Create New Workflow</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Workflow Name</label>
                <input
                  type="text"
                  className="w-full bg-background border border-border rounded px-3 py-2 text-text-primary"
                  placeholder="e.g., Daily Trading Automation"
                  value={newWorkflow.name}
                  onChange={(e) => setNewWorkflow({...newWorkflow, name: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm text-text-secondary mb-1">Description</label>
                <textarea
                  className="w-full bg-background border border-border rounded px-3 py-2 text-text-primary h-24"
                  placeholder="Describe what this workflow does..."
                  value={newWorkflow.description}
                  onChange={(e) => setNewWorkflow({...newWorkflow, description: e.target.value})}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button 
                className="btn-secondary"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleCreateWorkflow}
                disabled={!newWorkflow.name.trim()}
              >
                Create Workflow
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Workflows
